<script setup lang="ts">
/**
 * A grid of `NxChoiceTile`s to pick from — templates, recipes, layouts,
 * modules. Single choice (`v-model` is the value, radio semantics: the arrow
 * keys move AND select, one tab stop) or `multiple` (`v-model` is an array,
 * checkbox semantics: the arrow keys move focus, Space toggles). The grid
 * fills the width with tiles of at least `tileWidth`.
 */
import { computed, provide, ref, useTemplateRef } from 'vue';
import { choiceTileContextKey, type NxChoiceValue } from './context';

const props = withDefaults(
  defineProps<{
    multiple?: boolean;
    /** Accessible name of the group („Vorlage wählen“). */
    label?: string;
    /** Minimum tile width of the auto-fill grid. */
    tileWidth?: 'sm' | 'md' | 'lg';
  }>(),
  { multiple: false, label: undefined, tileWidth: 'md' }
);

const model = defineModel<NxChoiceValue | NxChoiceValue[] | null>({ default: null });

const multiple = computed(() => props.multiple);
const selectedList = computed<NxChoiceValue[]>(() => {
  const v = model.value;
  if (Array.isArray(v)) return v;
  return v === null || v === undefined ? [] : [v];
});

interface Entry {
  value: NxChoiceValue;
  disabled: () => boolean;
}
const entries = ref<Entry[]>([]);

function isSelected(value: NxChoiceValue): boolean {
  return selectedList.value.includes(value);
}

function toggle(value: NxChoiceValue) {
  if (props.multiple) {
    const list = selectedList.value;
    model.value = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  } else {
    model.value = value;
  }
}

function isTabStop(value: NxChoiceValue): boolean {
  if (props.multiple) return true;
  const enabled = entries.value.filter((e) => !e.disabled());
  const selected = enabled.find((e) => isSelected(e.value));
  return (selected ?? enabled[0])?.value === value;
}

function register(value: NxChoiceValue, disabled: () => boolean) {
  const entry: Entry = { value, disabled };
  entries.value = [...entries.value, entry];
  return () => {
    entries.value = entries.value.filter((e) => e !== entry);
  };
}

provide(choiceTileContextKey, { multiple, isSelected, toggle, isTabStop, register });

const rootRef = useTemplateRef<HTMLElement>('root');

function onKeydown(event: KeyboardEvent) {
  const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
  if (!keys.includes(event.key)) return;
  const tiles = [
    ...(rootRef.value?.querySelectorAll<HTMLElement>(
      '[data-choice-tile]:not([aria-disabled="true"])'
    ) ?? []),
  ];
  const index = tiles.indexOf(document.activeElement as HTMLElement);
  if (index < 0 || !tiles.length) return;
  event.preventDefault();
  let next = index;
  if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = tiles.length - 1;
  else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    next = (index + 1) % tiles.length;
  } else next = (index - 1 + tiles.length) % tiles.length;
  const tile = tiles[next]!;
  tile.focus();
  // Radio semantics: moving selects.
  if (!props.multiple) tile.click();
}
</script>

<template>
  <div
    ref="root"
    class="nx-choice-tile-group"
    :class="`tile-${props.tileWidth}`"
    :role="props.multiple ? 'group' : 'radiogroup'"
    :aria-label="props.label"
    data-testid="nx-choice-tile-group"
    @keydown="onKeydown"
  >
    <slot />
  </div>
</template>

<style scoped>
.nx-choice-tile-group {
  --tile-min: var(--nxt-panel-width-sm);

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--tile-min)), 1fr));
  gap: var(--nxt-space-sm);
}
.nx-choice-tile-group.tile-sm {
  --tile-min: var(--nxt-panel-width-xs);
}
.nx-choice-tile-group.tile-lg {
  --tile-min: var(--nxt-panel-width-md);
}
</style>
